/**
 * RetroBoardSummarizeButton — toolbar-slot component for the retro board.
 *
 * Rendered by WidgetFloatingToolbar via the preset's `toolbarSlot` field.
 * Receives { id, data } from the toolbar so it can access the board's cards
 * and fire the AI summarize action using the canvas agent.
 */

import { useCreateFlowSession } from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FLOW_SESSION_MODES } from "@cohesive/api-client";
import { Sparkles } from "lucide-react";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvasFloatingChat } from "../../../context/CanvasFloatingChatContext";
import { useCanvasRoute } from "../../../context/CanvasRouteContext";
import { RETRO_COLUMN_NAMES, type RetroCard } from "./index";

interface Props {
  id: string;
  data: Record<string, unknown>;
}

export function RetroBoardSummarizeButton({ data }: Props) {
  const { canvasId } = useCanvasRoute();
  const createFlowSession = useCreateFlowSession();
  const postMessage = usePostFlowMessage();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();

  const cards = Array.isArray(data.cards) ? (data.cards as RetroCard[]) : [];

  const handleSummarize = useCallback(async () => {
    if (!canvasId || cards.length === 0) return;

    const sections = ([0, 1, 2, 3] as const)
      .map((ci) => {
        const col = cards.filter((c) => c.columnIndex === ci);
        if (!col.length) return "";
        const items = col
          .map(
            (c) =>
              `  - ${c.content} (${c.votes.length} vote${c.votes.length !== 1 ? "s" : ""})`,
          )
          .join("\n");
        return `**${RETRO_COLUMN_NAMES[ci]}**\n${items}`;
      })
      .filter(Boolean)
      .join("\n\n");

    const prompt = `Here is our sprint retrospective:\n\n${sections}\n\nPlease:\n1. Write a brief overall summary\n2. Identify key themes and patterns\n3. Suggest concrete next steps based on the feedback\n\nCreate an artifact with the full analysis and place it on the canvas.`;

    try {
      const newSession = await createFlowSession.mutateAsync({
        mode: FLOW_SESSION_MODES.CANVAS_CHAT,
        class: "canvas",
        canvas_id: canvasId,
      });
      await postMessage.mutateAsync({
        flowSessionId: newSession.id,
        id: uuidv4(),
        content: prompt,
        referenced_entities: [],
      });
      openCanvasFloatingSession(newSession.id);
    } catch (err) {
      console.error(
        "[RetroBoardSummarizeButton] Failed to create summary session:",
        err,
      );
    }
  }, [
    canvasId,
    cards,
    createFlowSession,
    postMessage,
    openCanvasFloatingSession,
  ]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleSummarize();
          }}
          className="h-7 w-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-n-95/8 dark:hover:bg-n-95/10 transition-colors focus:outline-none"
          aria-label="Summarize retro"
        >
          <Sparkles className="h-[15px] w-[15px]" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">AI summary & next steps</TooltipContent>
    </Tooltip>
  );
}
